// Shared types + display helpers for the Driver Address Health module.
import type { ChipTone } from '../components/interviews/Chip'

export type AddressHealthRow = {
  driver_id: string
  driver_name: string | null
  primary_phone: string | null
  whatsapp_phone: string | null
  current_address: string | null
  onboarding_address: string | null
  google_maps_url: string | null
  suburb: string | null
  city: string | null
  province: string | null
  postal_code: string | null
  lat: number | null
  lng: number | null
  geocode_status: string | null
  geocode_confidence: string | null
  manually_verified: boolean | null
  address_health: string | null
  is_clean: boolean | null
  // Every flag below is non-NULL. is_missing flags only drivers with no address
  // row at all; needs_attention is the broader signal, true whenever
  // address_health is not clean (missing, pending, needs_review or failed).
  is_missing: boolean
  needs_attention: boolean
  // Plain English explanation of what to do, null when the address is clean.
  attention_reason: string | null
  driver_status: string | null
  updated_at: string | null
}

// Chip tone for an address_health status. Clean is green; pending is amber;
// missing, failed and needs_review are red because each needs a person to act.
export function addressHealthTone(status: string | null | undefined): ChipTone {
  switch (status) {
    case 'clean':
      return 'green'
    case 'pending':
      return 'amber'
    case 'missing':
    case 'failed':
    case 'needs_review':
      return 'red'
    default:
      return 'neutral'
  }
}

// Human label for an address_health status.
export function addressHealthLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown'
  return status
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// Label for the address action button, chosen by row state. A pending row
// already has an address captured, so the useful action is running the geocoder,
// not adding an address. A missing row needs an address added; a clean row can
// be updated; anything else is a general edit.
export function addressButtonLabel(row: AddressHealthRow): string {
  if (row.address_health === 'pending') return 'Run geocoder'
  if (row.is_missing) return 'Add address'
  if (row.is_clean === true) return 'Update address'
  return 'Edit address'
}

// The address to show for a driver: first non-empty of current, onboarding, or
// google maps url; '-' when all are blank.
export function listedAddress(row: AddressHealthRow): string {
  return (
    row.current_address ||
    row.onboarding_address ||
    row.google_maps_url ||
    '-'
  )
}
